import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { getSupabase } from './supabase';
import { LoadingCrest } from './LoadingCrest';
import { isPlayerRole, isStaffRole } from './staffAccess';
import { normalizeClubName } from './leagueClubs';
import type {
  LeagueClub,
  Lineup,
  Match,
  OfficialLeagueResult,
  OfficialLeagueScorer,
  OfficialLeagueStanding,
  Player,
  Relationship,
  Team,
} from './types';


const SMART_RESULTS_STORAGE_KEY = 'ecc_smart_results_2026_27';

function loadSmartImportedResults(): OfficialLeagueResult[] {
  try {
    const raw = localStorage.getItem(SMART_RESULTS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((row) => row && row.season === '2026/27' && row.division === 'Only Sport Ltd Premiership');
  } catch {
    return [];
  }
}

function mergeOfficialAndSmartResults(official: OfficialLeagueResult[], smart: OfficialLeagueResult[]) {
  const keyOf = (row: OfficialLeagueResult) => [
    row.match_date,
    normalizeClubName(row.home_team),
    normalizeClubName(row.away_team),
  ].join('|');
  const merged = new Map<string, OfficialLeagueResult>();
  smart.forEach((row) => merged.set(keyOf(row), row));
  official.forEach((row) => merged.set(keyOf(row), row)); // official verification always wins
  return [...merged.values()].sort((a, b) => b.match_date.localeCompare(a.match_date));
}

type DataContextValue = {
  team: Team | null;
  teams: Team[];
  players: Player[];
  matches: Match[];
  relationships: Relationship[];
  lineups: Lineup[];
  leagueClubs: LeagueClub[];
  officialStandings: OfficialLeagueStanding[];
  officialScorers: OfficialLeagueScorer[];
  officialResults: OfficialLeagueResult[];
  officialLeagueSyncedAt: string | null;
  loading: boolean;
  error: string;
  refresh: () => Promise<void>;
  setActiveTeam: (teamId: string) => Promise<void>;
  createMatch: (input: {
    match_date: string;
    opponent: string;
    competition: string;
  }) => Promise<{ ok: boolean; message: string }>;
  createPlayer: (input: {
    name: string;
    squad_number: number | null;
    position: string;
    photo_url?: string;
  }) => Promise<{ ok: boolean; message: string }>;
  updatePlayer: (
    playerId: string,
    patch: Partial<Pick<Player,
      'name' | 'squad_number' | 'position' | 'potential' | 'current_form' |
      'fitness' | 'morale' | 'photo_url' | 'bio' | 'notes' |
      'attributes' | 'mindset' | 'abilities'
    >>,
  ) => Promise<{ ok: boolean; message: string; player?: Player }>;
  uploadPlayerPhoto: (
    playerId: string,
    file: Blob,
    extension?: string,
  ) => Promise<{ ok: boolean; message: string; photoUrl?: string }>;
  deletePlayer: (playerId: string) => Promise<{ ok: boolean; message: string }>;
  saveRelationship: (input: {
    playerA: string;
    playerB: string;
    score: number;
    notes?: string;
  }) => Promise<{ ok: boolean; message: string }>;
  deleteRelationship: (relationshipId: string) => Promise<{ ok: boolean; message: string }>;
  saveLineup: (input: {
    name: string;
    formation: string;
    slots: Record<string, string | null>;
  }) => Promise<{ ok: boolean; message: string }>;
  publishLineup: (input: {
    name: string;
    formation: string;
    slots: Record<string, string | null>;
    publish: boolean;
  }) => Promise<{ ok: boolean; message: string }>;
};

const DataContext = createContext<DataContextValue | null>(null);
const ACTIVE_TEAM_KEY = 'ecc2_active_team';
const VERIFIED_ROSTER_VERSION = 'polonia-squad-2026-v2';
const VERIFIED_POLONIA_PLAYERS = [
  { name: 'Kamil Sobota', squad_number: 1, position: 'GK' },
  { name: 'Tomasz Kmin', squad_number: 2, position: 'GK' },
  { name: 'Janusz Ptak', squad_number: null, position: 'CB' },
  { name: 'Patryk Ładyński', squad_number: null, position: 'LB' },
  { name: 'Dawid Maślanka', squad_number: null, position: 'CDM' },
  { name: 'Jakub Kwiatkowski', squad_number: null, position: 'CDM' },
  { name: 'Artur Jatocha', squad_number: null, position: 'RB' },
  { name: 'Michał Jadamus', squad_number: null, position: 'RW' },
  { name: 'Olens Shulim', squad_number: null, position: 'LW' },
  { name: 'Oskar Kulig', squad_number: null, position: 'CAM' },
  { name: 'Dominik Piwowar', squad_number: null, position: 'CAM' },
  { name: 'Marcin Śliwa', squad_number: null, position: 'CM' },
  { name: 'Krzysztof Kierys', squad_number: null, position: 'TBA' },
  { name: 'Piotr Burzyński', squad_number: null, position: 'CB' },
  { name: 'Marek Krawczuk', squad_number: null, position: 'LB' },
  { name: 'Bartosz Nowak', squad_number: null, position: 'RW' },
  { name: 'Dominik Lichon', squad_number: null, position: 'LW' },
] as const;

function normalisePlayerName(value: string) {
  return value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ł/g, 'l')
    .replace(/\s+/g, ' ');
}

export function DataProvider({ children }: { children: ReactNode }) {
  const [teams, setTeams] = useState<Team[]>([]);
  const [team, setTeam] = useState<Team | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [matches, setMatches] = useState<Match[]>([]);
  const [relationships, setRelationships] = useState<Relationship[]>([]);
  const [lineups, setLineups] = useState<Lineup[]>([]);
  const [leagueClubs, setLeagueClubs] = useState<LeagueClub[]>([]);
  const [officialStandings, setOfficialStandings] = useState<OfficialLeagueStanding[]>([]);
  const [officialScorers, setOfficialScorers] = useState<OfficialLeagueScorer[]>([]);
  const [officialResults, setOfficialResults] = useState<OfficialLeagueResult[]>([]);
  const [officialLeagueSyncedAt, setOfficialLeagueSyncedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadTeamData = useCallback(async (activeTeam: Team) => {
    const client = getSupabase();
    if (!client) return;
    setLoading(true);
    setError('');

    const season = activeTeam.season || '2026/27';
    const division = 'Only Sport Ltd Premiership';
    const playerMode = isPlayerRole(activeTeam.role);
    const staffMode = isStaffRole(activeTeam.role);
    const sanitizedRosterMode = playerMode || staffMode;

    const playersQuery = sanitizedRosterMode
      ? client.from('player_public_profiles').select('*').eq('team_id', activeTeam.id).order('name')
      : client.from('players').select('*').eq('team_id', activeTeam.id).order('name');

    const matchesQuery = playerMode
      ? client.from('matches').select('*').eq('team_id', activeTeam.id).order('match_date', { ascending: false })
      : client.from('matches').select('*,contributions(*)').eq('team_id', activeTeam.id).order('match_date', { ascending: false });

    const relationsQuery = sanitizedRosterMode
      ? Promise.resolve({ data: [], error: null })
      : client.from('player_relationships').select('*').eq('team_id', activeTeam.id);

    const lineupsQuery = playerMode
      ? client.from('lineups').select('*').eq('team_id', activeTeam.id).eq('publication_status', 'published').order('updated_at', { ascending: false })
      : client.from('lineups').select('*').eq('team_id', activeTeam.id).order('updated_at', { ascending: false });

    const [
      playersResult,
      matchesResult,
      relationsResult,
      lineupsResult,
      leagueClubsResult,
      officialStandingsResult,
      officialScorersResult,
      officialResultsResult,
    ] = await Promise.all([
      playersQuery,
      matchesQuery,
      relationsQuery,
      lineupsQuery,
      client
        .from('league_clubs')
        .select('*')
        .eq('season', season)
        .eq('division', division)
        .eq('active', true)
        .order('sort_order'),
      client
        .from('official_league_standings')
        .select('*')
        .eq('season', season)
        .eq('division', division)
        .order('position'),
      client
        .from('official_league_scorers')
        .select('*')
        .eq('season', season)
        .eq('division', division)
        .order('rank'),
      client
        .from('official_league_results')
        .select('*')
        .eq('season', season)
        .eq('division', division)
        .order('match_date', { ascending: false }),
    ]);

    const firstError =
      playersResult.error ||
      matchesResult.error ||
      relationsResult.error ||
      lineupsResult.error;

    if (firstError) setError(firstError.message);

    let resolvedPlayers = (playersResult.data ?? []) as Player[];

    // One-time, duplicate-safe bootstrap of players publicly verified by GDSFC.
    // The local marker prevents intentionally removed players from being recreated on normal reloads.
    const rosterSeedKey = `ecc_verified_roster_${activeTeam.id}_${VERIFIED_ROSTER_VERSION}`;
    if (!playerMode && !staffMode && !localStorage.getItem(rosterSeedKey)) {
      const existingNames = new Set(resolvedPlayers.map((player) => normalisePlayerName(player.name)));
      const missingVerifiedPlayers = VERIFIED_POLONIA_PLAYERS.filter(
        (candidate) => !existingNames.has(normalisePlayerName(candidate.name)),
      );

      const rosterByName = new Map(
        VERIFIED_POLONIA_PLAYERS.map((candidate) => [normalisePlayerName(candidate.name), candidate]),
      );

      for (const existingPlayer of resolvedPlayers) {
        const rosterPlayer = rosterByName.get(normalisePlayerName(existingPlayer.name));
        if (!rosterPlayer) continue;

        const needsPositionUpdate =
          !existingPlayer.position ||
          existingPlayer.position === '—' ||
          existingPlayer.position === 'TBA' ||
          (
            (existingPlayer.name === 'Bartosz Nowak' || existingPlayer.name === 'Oskar Kulig') &&
            existingPlayer.position === 'ST'
          );

        const needsSquadNumberUpdate =
          rosterPlayer.squad_number !== null &&
          existingPlayer.squad_number !== rosterPlayer.squad_number;

        if (needsPositionUpdate || needsSquadNumberUpdate) {
          await client
            .from('players')
            .update({
              ...(needsPositionUpdate ? { position: rosterPlayer.position } : {}),
              ...(needsSquadNumberUpdate ? { squad_number: rosterPlayer.squad_number } : {}),
            })
            .eq('team_id', activeTeam.id)
            .eq('id', existingPlayer.id);
        }
      }

      if (missingVerifiedPlayers.length) {
        const { error: rosterInsertError } = await client.from('players').insert(
          missingVerifiedPlayers.map((candidate) => ({
            team_id: activeTeam.id,
            name: candidate.name,
            squad_number: candidate.squad_number,
            position: candidate.position,
            potential: 60,
            current_form: 6,
            fitness: 100,
            morale: 100,
            photo_url: null,
            bio: {
              roster_source: 'Polonia Glasgow AFC roster supplied by coach + club Facebook screenshots',
              roster_source_status: 'coach_verified',
              original_position_label:
                candidate.position === 'LB' ? 'FBL' :
                candidate.position === 'RB' ? 'FBR' :
                candidate.position === 'CDM' ? 'DMF' :
                candidate.position === 'RW' ? 'Winger R' :
                candidate.position === 'LW' ? 'Winger L' :
                candidate.position === 'CAM' ? 'AMF' :
                candidate.position === 'CM' ? 'CMF' :
                candidate.position,
            },
            notes: '',
            attributes: {},
            mindset: {},
            abilities: {},
          })),
        );

        if (!rosterInsertError) {
          const refreshedPlayers = await client
            .from('players')
            .select('*')
            .eq('team_id', activeTeam.id)
            .order('name');
          if (!refreshedPlayers.error) {
            resolvedPlayers = (refreshedPlayers.data ?? []) as Player[];
          }
          localStorage.setItem(rosterSeedKey, '1');
        }
      } else {
        const refreshedPlayers = await client
          .from('players')
          .select('*')
          .eq('team_id', activeTeam.id)
          .order('name');
        if (!refreshedPlayers.error) {
          resolvedPlayers = (refreshedPlayers.data ?? []) as Player[];
        }
        localStorage.setItem(rosterSeedKey, '1');
      }
    }

    const visiblePlayers = sanitizedRosterMode
      ? resolvedPlayers.map((player) => ({
          ...player,
          potential: null,
          current_form: null,
          fitness: null,
          morale: null,
          notes: null,
          attributes: null,
          mindset: null,
          abilities: null,
        }))
      : resolvedPlayers;

    setPlayers(visiblePlayers);
    setMatches((matchesResult.data ?? []) as Match[]);
    setRelationships(sanitizedRosterMode ? [] : (relationsResult.data ?? []) as Relationship[]);
    setLineups((lineupsResult.data ?? []) as Lineup[]);
    setLeagueClubs((leagueClubsResult.data ?? []) as LeagueClub[]);
    const syncedStandings = officialStandingsResult.error ? [] : ((officialStandingsResult.data ?? []) as OfficialLeagueStanding[]);
    const syncedScorers = officialScorersResult.error ? [] : ((officialScorersResult.data ?? []) as OfficialLeagueScorer[]);
    const syncedResults = officialResultsResult.error ? [] : ((officialResultsResult.data ?? []) as OfficialLeagueResult[]);
    const smartImportedResults = loadSmartImportedResults();
    const mergedResults = mergeOfficialAndSmartResults(syncedResults, smartImportedResults);
    setOfficialStandings(syncedStandings);
    setOfficialScorers(syncedScorers);
    setOfficialResults(mergedResults);
    setOfficialLeagueSyncedAt(
      syncedStandings[0]?.synced_at ||
      syncedScorers[0]?.synced_at ||
      syncedResults[0]?.synced_at ||
      smartImportedResults[0]?.synced_at ||
      null,
    );
    setLoading(false);
  }, []);

  const initialize = useCallback(async () => {
    const client = getSupabase();
    if (!client) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const { data: auth } = await client.auth.getUser();
    const user = auth.user;
    if (!user) {
      setLoading(false);
      return;
    }

    const { data, error: membershipError } = await client
      .from('team_members')
      .select('role,team_id,player_id,teams(*)')
      .eq('user_id', user.id);

    if (membershipError) {
      setError(membershipError.message);
      setLoading(false);
      return;
    }

    const resolvedTeams = (data ?? [])
      .map((entry: any) => ({ ...entry.teams, role: entry.role, player_id: entry.player_id || null }))
      .filter(Boolean) as Team[];

    setTeams(resolvedTeams);
    const stored = localStorage.getItem(ACTIVE_TEAM_KEY);
    const active =
      resolvedTeams.find((candidate) => candidate.id === stored) ??
      resolvedTeams[0] ??
      null;

    setTeam(active);
    if (active) {
      localStorage.setItem(ACTIVE_TEAM_KEY, active.id);
      await loadTeamData(active);
    } else {
      setLoading(false);
    }
  }, [loadTeamData]);

  useEffect(() => {
    void initialize();
  }, [initialize]);

  const refresh = useCallback(async () => {
    if (team) await loadTeamData(team);
    else await initialize();
  }, [initialize, loadTeamData, team]);

  const setActiveTeam = useCallback(
    async (teamId: string) => {
      const next = teams.find((candidate) => candidate.id === teamId);
      if (!next) return;
      localStorage.setItem(ACTIVE_TEAM_KEY, next.id);
      setTeam(next);
      await loadTeamData(next);
    },
    [loadTeamData, teams],
  );



  const createPlayer = useCallback(
    async (input: {
      name: string;
      squad_number: number | null;
      position: string;
      photo_url?: string;
    }) => {
      const client = getSupabase();
      if (!client || !team) return { ok: false, message: 'No active team' };
      if (!input.name.trim()) return { ok: false, message: 'Player name is required' };

      const payload = {
        team_id: team.id,
        name: input.name.trim(),
        squad_number: input.squad_number,
        position: input.position.trim() || '—',
        potential: 60,
        current_form: 6,
        fitness: 100,
        morale: 100,
        photo_url: input.photo_url?.trim() || null,
        bio: {},
        notes: '',
        attributes: {},
        mindset: {},
        abilities: {},
      };

      const { error: insertError } = await client.from('players').insert(payload);
      if (insertError) return { ok: false, message: insertError.message };

      await loadTeamData(team);
      return { ok: true, message: 'Player created' };
    },
    [loadTeamData, team],
  );



  const updatePlayer = useCallback(
    async (
      playerId: string,
      patch: Partial<Pick<Player,
        'name' | 'squad_number' | 'position' | 'potential' | 'current_form' |
        'fitness' | 'morale' | 'photo_url' | 'bio' | 'notes' |
        'attributes' | 'mindset' | 'abilities'
      >>,
    ) => {
      const client = getSupabase();
      if (!client || !team) return { ok: false, message: 'No active team' };

      const cleanPatch = {
        ...patch,
        ...(typeof patch.name === 'string' ? { name: patch.name.trim() } : {}),
        ...(typeof patch.position === 'string' ? { position: patch.position.trim() || '—' } : {}),
        ...(typeof patch.photo_url === 'string' ? { photo_url: patch.photo_url.trim() || null } : {}),
        updated_at: new Date().toISOString(),
      };

      if ('name' in cleanPatch && !cleanPatch.name) {
        return { ok: false, message: 'Player name is required' };
      }

      const { data, error: updateError } = await client
        .from('players')
        .update(cleanPatch)
        .eq('team_id', team.id)
        .eq('id', playerId)
        .select('*')
        .single();

      if (updateError) return { ok: false, message: updateError.message };

      await loadTeamData(team);
      return { ok: true, message: 'Player updated', player: data as Player };
    },
    [loadTeamData, team],
  );

  const uploadPlayerPhoto = useCallback(
    async (playerId: string, file: Blob, extension = 'jpg') => {
      const client = getSupabase();
      if (!client || !team) return { ok: false, message: 'No active team' };

      const safeExt = extension.replace(/[^a-z0-9]/gi, '').toLowerCase() || 'jpg';
      const objectPath = `${team.id}/${playerId}/profile-${Date.now()}.${safeExt}`;

      const { error: uploadError } = await client.storage
        .from('player-photos')
        .upload(objectPath, file, {
          cacheControl: '3600',
          upsert: false,
          contentType: file.type || 'image/jpeg',
        });

      if (uploadError) return { ok: false, message: uploadError.message };

      const { data: publicUrlData } = client.storage
        .from('player-photos')
        .getPublicUrl(objectPath);

      const photoUrl = publicUrlData.publicUrl;
      if (!photoUrl) return { ok: false, message: 'Could not create photo URL' };

      return { ok: true, message: 'Photo uploaded', photoUrl };
    },
    [team],
  );

  const deletePlayer = useCallback(
    async (playerId: string) => {
      const client = getSupabase();
      if (!client || !team) return { ok: false, message: 'No active team' };

      // Remove dependent records first to avoid FK errors on installations without cascade rules.
      const contributionDelete = await client.from('contributions').delete().eq('player_id', playerId);
      if (contributionDelete.error) return { ok: false, message: contributionDelete.error.message };

      const relationADelete = await client.from('player_relationships').delete().eq('player_a', playerId);
      if (relationADelete.error) return { ok: false, message: relationADelete.error.message };

      const relationBDelete = await client.from('player_relationships').delete().eq('player_b', playerId);
      if (relationBDelete.error) return { ok: false, message: relationBDelete.error.message };

      const playerDelete = await client
        .from('players')
        .delete()
        .eq('team_id', team.id)
        .eq('id', playerId);

      if (playerDelete.error) return { ok: false, message: playerDelete.error.message };

      await loadTeamData(team);
      return { ok: true, message: 'Player removed' };
    },
    [loadTeamData, team],
  );

  const createMatch = useCallback(
    async (input: { match_date: string; opponent: string; competition: string }) => {
      const client = getSupabase();
      if (!client || !team) return { ok: false, message: 'No active team' };
      const { error: insertError } = await client.from('matches').insert({
        team_id: team.id,
        match_date: input.match_date,
        opponent: input.opponent.trim(),
        competition: input.competition.trim(),
        goals_for: null,
        goals_against: null,
      });
      if (insertError) return { ok: false, message: insertError.message };
      await loadTeamData(team);
      return { ok: true, message: 'Match created' };
    },
    [loadTeamData, team],
  );

  const saveRelationship = useCallback(
    async (input: { playerA: string; playerB: string; score: number; notes?: string }) => {
      const client = getSupabase();
      if (!client || !team) return { ok: false, message: 'No active team' };

      const playerA = input.playerA.trim();
      const playerB = input.playerB.trim();
      const score = Math.max(0, Math.min(100, Math.round(Number(input.score))));

      if (!playerA || !playerB) return { ok: false, message: 'Select two players' };
      if (playerA === playerB) return { ok: false, message: 'Choose two different players' };
      if (!Number.isFinite(score)) return { ok: false, message: 'Chemistry score must be 0–100' };

      // A relationship is symmetric: A↔B and B↔A are the same pair.
      const canonicalA = playerA < playerB ? playerA : playerB;
      const canonicalB = playerA < playerB ? playerB : playerA;

      const existing = relationships.find((relationship) => {
        const a = relationship.player_a;
        const b = relationship.player_b;
        return (a === canonicalA && b === canonicalB) || (a === canonicalB && b === canonicalA);
      });

      const payload = {
        team_id: team.id,
        player_a: canonicalA,
        player_b: canonicalB,
        score,
        // DB compatibility: player_relationships_relation_check only accepts
        // strong | good | neutral | difficult | conflict.
        // The visible UI classification still comes entirely from the 0–100 score.
        relation: score >= 60 ? 'strong' : score >= 30 ? 'neutral' : 'difficult',
        notes: input.notes?.trim() || null,
      };

      if (existing?.id) {
        const { error: updateError } = await client
          .from('player_relationships')
          .update(payload)
          .eq('team_id', team.id)
          .eq('id', existing.id);

        if (updateError) return { ok: false, message: updateError.message };
      } else {
        const { error: insertError } = await client
          .from('player_relationships')
          .insert(payload);

        if (insertError) return { ok: false, message: insertError.message };
      }

      await loadTeamData(team);
      return { ok: true, message: existing ? 'Chemistry updated' : 'Chemistry connection created' };
    },
    [loadTeamData, relationships, team],
  );

  const deleteRelationship = useCallback(
    async (relationshipId: string) => {
      const client = getSupabase();
      if (!client || !team) return { ok: false, message: 'No active team' };
      if (!relationshipId) return { ok: false, message: 'Relationship ID missing' };

      const { error: deleteError } = await client
        .from('player_relationships')
        .delete()
        .eq('team_id', team.id)
        .eq('id', relationshipId);

      if (deleteError) return { ok: false, message: deleteError.message };

      await loadTeamData(team);
      return { ok: true, message: 'Chemistry connection removed' };
    },
    [loadTeamData, team],
  );

  const saveLineup = useCallback(
    async (input: { name: string; formation: string; slots: Record<string, string | null> }) => {
      const client = getSupabase();
      if (!client || !team) return { ok: false, message: 'No active team' };
      const { error: insertError } = await client.from('lineups').insert({
        team_id: team.id,
        name: input.name.trim() || 'Matchday XI',
        formation: input.formation,
        slots: input.slots,
      });
      if (insertError) return { ok: false, message: insertError.message };
      await loadTeamData(team);
      return { ok: true, message: 'Lineup saved' };
    },
    [loadTeamData, team],
  );

  const publishLineup = useCallback(
    async (input: {
      name: string;
      formation: string;
      slots: Record<string, string | null>;
      publish: boolean;
    }) => {
      const client = getSupabase();
      if (!client || !team) return { ok: false, message: 'No active team' };

      const { error: clearError } = await client
        .from('lineups')
        .update({ publication_status: 'draft', published_at: null, published_by: null })
        .eq('team_id', team.id)
        .eq('publication_status', 'published');

      if (clearError) return { ok: false, message: clearError.message };

      if (input.publish) {
        const { data: auth } = await client.auth.getUser();
        const { error: publishError } = await client.from('lineups').insert({
          team_id: team.id,
          name: input.name.trim() || 'Matchday XI',
          formation: input.formation,
          slots: input.slots,
          publication_status: 'published',
          published_at: new Date().toISOString(),
          published_by: auth.user?.id ?? null,
        });
        if (publishError) return { ok: false, message: publishError.message };
      }

      await loadTeamData(team);
      return { ok: true, message: input.publish ? 'Lineup published' : 'Lineup returned to draft' };
    },
    [loadTeamData, team],
  );

  const value = useMemo(
    () => ({
      team,
      teams,
      players,
      matches,
      relationships,
      lineups,
      leagueClubs,
      officialStandings,
      officialScorers,
      officialResults,
      officialLeagueSyncedAt,
      loading,
      error,
      refresh,
      setActiveTeam,
      createMatch,
      createPlayer,
      updatePlayer,
      uploadPlayerPhoto,
      deletePlayer,
      saveRelationship,
      deleteRelationship,
      saveLineup,
      publishLineup,
    }),
    [
      team,
      teams,
      players,
      matches,
      relationships,
      lineups,
      leagueClubs,
      officialStandings,
      officialScorers,
      officialResults,
      officialLeagueSyncedAt,
      loading,
      error,
      refresh,
      setActiveTeam,
      createMatch,
      createPlayer,
      updatePlayer,
      uploadPlayerPhoto,
      deletePlayer,
      saveRelationship,
      deleteRelationship,
      saveLineup,
      publishLineup,
    ],
  );

  return (
    <DataContext.Provider value={value}>
      {loading && <LoadingCrest />}
      {children}
    </DataContext.Provider>
  );
}

export function useData() {
  const context = useContext(DataContext);
  if (!context) throw new Error('useData must be used inside DataProvider');
  return context;
}
